import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import i18n from '@/core/i18n';
import { roundToPrecision } from '@/helpers/finance';
import {
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { formatDateToNonISOFormat, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import {
  GenerateBillAddedProjectListItem,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
  GenerateBillSalaryAdjustmentListItem,
} from '../types';
import { BillEntityTypeDTO } from '../types/dto/bill-entity-type.dto';
import { CreateBillItemRequest, CreateBillRequest, CreateBillsRequest } from '../types/dto/request';
import { calculateLineAmount, isSalaryAdjustmentEmpty, SALARY_LINE_QUANTITY } from '../utils';

const mapProjectToBillItem = (
  project: GenerateBillMemberProjectListItem
): CreateBillItemRequest => {
  const projectId = parseInt(project.id, 10);
  const roundedAmount = roundToPrecision(project.total, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const roundedRate = roundToPrecision(
    project.effectiveRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: projectId,
    entityType: BillEntityTypeDTO.PROJECT,
    quantity: project.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

const mapTimeOffToBillItem = (
  timeOff: GenerateBillMemberTimeOffListItem
): CreateBillItemRequest => {
  const roundedAmount = roundToPrecision(timeOff.total, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const roundedRate = roundToPrecision(
    timeOff.effectiveRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: timeOff.timeOffTypeId,
    entityType: BillEntityTypeDTO.TIME_OFF_TYPE,
    quantity: timeOff.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

const mapAddedProjectToBillItem = (
  addedProject: GenerateBillAddedProjectListItem
): CreateBillItemRequest | null => {
  if (!addedProject.projectId) return null;

  const roundedAmount = calculateLineAmount({
    hours: addedProject.hours,
    rate: addedProject.effectiveRate,
  });
  const roundedRate = roundToPrecision(
    addedProject.effectiveRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: addedProject.projectId,
    entityType: BillEntityTypeDTO.PROJECT,
    quantity: addedProject.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

const mapOverheadToBillItem = (
  overhead: GenerateBillMemberOverheadListItem
): CreateBillItemRequest => {
  const roundedAmount = roundToPrecision(
    overhead.effectiveRate,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

  return {
    entityType: BillEntityTypeDTO.OVERHEAD,
    description: overhead.description,
    amount: roundedAmount.toString(),
  };
};

/**
 * The salary line takes one of two shapes depending on what else is on the bill.
 *
 * When it is the **only** line (no projects / time-offs / overheads selected), it ships as a
 * dedicated SALARY item: the backend books it against the member's department with the
 * member's name as the description, quantity 1, and no customer/project reference.
 *
 * When it sits **alongside** other lines it is a reconciliation adjustment, so it ships as an
 * ADJUSTMENT item — the same flat amount + description shape as an overhead, but mapped to its
 * own "Adjustment" product/service in QuickBooks so accountants can tell a salary true-up apart
 * from a genuine overhead. In that case the amount may legitimately be negative (selected lines
 * exceeding the salary).
 */
const mapSalaryAdjustmentToBillItem = (
  salaryAdjustment: GenerateBillSalaryAdjustmentListItem,
  { isOnlyLine }: { isOnlyLine: boolean }
): CreateBillItemRequest => {
  const roundedAmount = roundToPrecision(
    salaryAdjustment.adjustmentAmount,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );
  const amount = roundedAmount.toString();

  if (isOnlyLine) {
    return {
      entityType: BillEntityTypeDTO.SALARY,
      quantity: SALARY_LINE_QUANTITY.toString(),
      unitPrice: amount,
      amount,
    };
  }

  return {
    entityType: BillEntityTypeDTO.ADJUSTMENT,
    description: i18n.t('authenticated.bills:monthlySalary.adjustmentLabel'),
    amount,
  };
};

const mapMemberToBillRequest = (
  member: GenerateBillListItem,
  billingPeriodString: string
): CreateBillRequest => {
  const dateRange = parseDateRange(billingPeriodString);

  if (!dateRange?.startDate || !dateRange?.endDate) {
    throw new Error('Invalid billing period');
  }

  const projectItems = member.memberProjects.map(mapProjectToBillItem);
  const addedProjectItems = member.memberAddedProjects
    .map(mapAddedProjectToBillItem)
    .filter((item): item is CreateBillItemRequest => item !== null);
  const timeOffItems = member.memberTimeOffs.map(mapTimeOffToBillItem);
  const overheadItems = member.memberOverheads.map(mapOverheadToBillItem);

  const hasOtherItems =
    projectItems.length > 0 ||
    addedProjectItems.length > 0 ||
    timeOffItems.length > 0 ||
    overheadItems.length > 0;
  // A zero adjustment means the selected lines already equal the salary — there is nothing to
  // reconcile, so the line is omitted rather than sent as a 0.00 item.
  const salaryAdjustmentItems =
    member.memberSalaryAdjustment &&
    !isSalaryAdjustmentEmpty(member.memberSalaryAdjustment.adjustmentAmount)
      ? [
          mapSalaryAdjustmentToBillItem(member.memberSalaryAdjustment, {
            isOnlyLine: !hasOtherItems,
          }),
        ]
      : [];

  return {
    memberId: member.member.id,
    startDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(dateRange.startDate)),
    endDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(dateRange.endDate)),
    currencyCode: member.currencyCode,
    items: [
      ...projectItems,
      ...addedProjectItems,
      ...timeOffItems,
      ...overheadItems,
      ...salaryAdjustmentItems,
    ],
  };
};

export const mapGenerateBillsToRequest = (
  billMembers: GenerateBillListItem[],
  billingPeriod: string
): CreateBillsRequest => {
  const bills: CreateBillRequest[] = billMembers.map(member =>
    mapMemberToBillRequest(member, billingPeriod)
  );

  return {
    bills,
  };
};
